import { useState } from 'react';

import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconEuro } from '@iwoca/icons';
import { ErrorMessage, Input } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import { getNumericValueFromString } from './getNumericValueFromString';

type TInputChangeEvent = React.ChangeEvent<HTMLInputElement>;

export const TurnoverField = ({ defaultValue }: { defaultValue: number }) => {
    const intl = useIntl();
    const [turnover, setTurnover] = useState(defaultValue);
    const [error, setError] = useState(false);

    const validate = (event: TInputChangeEvent) => {
        const input = event.target;
        const numericValue = getNumericValueFromString(event.target.value);

        if (input.validity.valueMissing || numericValue < 1) {
            inputSetCustomValidityAmountTooLow(input);
            setError(true);
        } else {
            setError(false);
        }
    };

    // Since the input type is text, it does not natively support min amount validity.
    // Setting custom validity here invalidates the parent form on submission.
    const inputSetCustomValidityAmountTooLow = (
        input: TInputChangeEvent['target']
    ) => {
        input.setCustomValidity(
            intl.formatMessage(
                {
                    id: '_.createNewApplicationForm.minTurnoverError',
                    defaultMessage:
                        'Please enter an amount greater than {minAmount}'
                },
                { minAmount: 0 }
            )
        );
    };

    const handleChange = (event: TInputChangeEvent) => {
        const numericValue = getNumericValueFromString(event.target.value);

        if (isNaN(numericValue)) {
            setTurnover(0);
            const input = event.target;
            inputSetCustomValidityAmountTooLow(input);
        } else {
            setTurnover(numericValue);
        }
        validate(event);
    };

    return (
        <div className="flex w-full flex-col gap-s">
            <Input.Root id="turnover">
                <Input.Label>
                    <FormattedMessage
                        id="_.topup_choice.last_12_months_turnover"
                        defaultMessage="What's your last 12 months turnover?"
                    />
                </Input.Label>
                <Input.Control
                    type="text"
                    required
                    name="turnover"
                    iconLeft={<IconEuro className="size-[20px]" />}
                    onChange={handleChange}
                    onInvalid={validate}
                    value={intl.formatNumber(turnover)}
                />
                {error && (
                    <Input.Status>
                        <ErrorMessage>
                            <FormattedMessage
                                id="_.createNewApplicationForm.minTurnoverError"
                                defaultMessage="Please enter an amount greater than {minAmount}"
                                values={{
                                    minAmount: intlFormatCurrency({
                                        intl,
                                        value: 0
                                    })
                                }}
                            />
                        </ErrorMessage>
                    </Input.Status>
                )}
            </Input.Root>
        </div>
    );
};
